<template>
  <div v-if="editor">
    <div class="backgroundColor toolbar">
      <!-- Fonts -->
      <select
        :id="'textEditorFonts' + propsObj.id + (isTranslation ? 'Translation' : '')"
        class="toolbarSelect"
        v-tooltip="{ content: $t('elementsTextTranslation.font'), theme: 'info-tooltip' }"
        @input="changeFont($event.target.value)"
      >
        <option
          v-for="font in fonts"
          :class="font.class"
          :key="font.name"
          :value="font.class"
          :id="'textEditorFont' + font.name"
          :selected="isActive(font.active)"
        >
          {{ font.name }}
        </option>
      </select>
      <!-- Headings -->
      <select
        :id="'textEditorHeadings' + propsObj.id + (isTranslation ? 'Translation' : '')"
        class="toolbarSelect"
        v-tooltip="{ content: $t('elementsTextTranslation.header'), theme: 'info-tooltip' }"
        @input="changeHeading($event.target.value)"
      >
        <option
          v-for="h in headings"
          :style="{ 'font-size': h.fontSize }"
          :key="h.level"
          :value="h.level"
          :id="'textEditorHeading' + h.level"
          :selected="isActive(h.active)"
        >
          {{ h.name }}
        </option>
      </select>
      <!-- Basic Textstyle -->
      <i
        :class="{ isActive: isActive(BOLD) }"
        class="fas fa-bold toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.bold'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().toggleBold().run()"
      ></i>
      <i
        :class="{ isActive: isActive(ITALIC) }"
        class="fas fa-italic toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.italic'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().toggleItalic().run()"
      ></i>
      <i
        :class="{ isActive: isActive(UNDERLINE) }"
        class="fas fa-underline toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.underline'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().toggleUnderline().run()"
      ></i>
      <i
        :class="{ isActive: isActive(STRIKETHROUGH) }"
        class="fas fa-strikethrough toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.strike'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().toggleStrike().run()"
      ></i>
      <!-- Alignment -->
      <i
        :class="{ isActive: isActive(ALIGN_LEFT) }"
        class="fas fa-align-left toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.align'), theme: 'info-tooltip' }"
        @click="changeTextAlign('left')"
      ></i>
      <i
        :class="{ isActive: isActive(ALIGN_CENTER) }"
        class="fas fa-align-center toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.alignCenter'), theme: 'info-tooltip' }"
        @click="changeTextAlign('center')"
      ></i>
      <i
        :class="{ isActive: isActive(ALIGN_RIGHT) }"
        class="fas fa-align-right toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.alignRight'), theme: 'info-tooltip' }"
        @click="changeTextAlign('right')"
      ></i>
      <i
        :class="{ isActive: isActive(ALIGN_JUSTIFY) }"
        class="fas fa-align-justify toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.alignJustify'), theme: 'info-tooltip' }"
        @click="changeTextAlign('justify')"
      ></i>
      <!-- Lists -->
      <i
        :class="{ isActive: isActive(ORDERED_LIST) }"
        class="fas fa-list-ol toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.listOrdered'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().toggleOrderedList().run()"
      ></i>
      <i
        :class="{ isActive: isActive(UNORDERED_LIST) }"
        class="fas fa-list-ul toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.listBullet'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().toggleBulletList().run()"
      ></i>
      <!-- Sub-/Superscript -->
      <i
        :class="{ isActive: isActive(SUBSCRIPT) }"
        class="fas fa-subscript toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.scriptSub'), theme: 'info-tooltip' }"
        @click="changeSubscript()"
      ></i>
      <i
        :class="{ isActive: isActive(SUPERSCRIPT) }"
        class="fas fa-superscript toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.scriptSuper'), theme: 'info-tooltip' }"
        @click="changeSuperscript()"
      ></i>
      <!-- Indentation -->
      <i
        class="fas fa-outdent toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.indentMinus'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().outdent().run()"
      ></i>
      <i
        class="fas fa-indent toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.indentPlus'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().indent().run()"
      ></i>
      <!-- Colors -->
      <div
        :class="{ colorIconsActive: showColors, colorIconsInactive: !showColors }"
        class="iconBox"
        @click="toggleColorsModal"
      >
        <i
          class="fas fa-pencil-alt toolbarIcon colorIcons"
          :style="{ color: activeColor }"
          v-tooltip="{ content: $t('elementsTextTranslation.color'), theme: 'info-tooltip' }"
        ></i>
      </div>
      <div v-if="showColors" class="modal">
        <span
          v-for="color in colors"
          :key="'color_' + color"
          role="button"
          class="colorSquare"
          :style="{ 'background-color': color }"
          @click="pickColor(color)"
        ></span>
      </div>
      <div
        :class="{ colorIconsActive: showBackgroundColors, colorIconsInactive: !showBackgroundColors }"
        class="iconBox"
        @click="toggleBackgroundColorsModal"
      >
        <i
          class="fas fa-highlighter toolbarIcon colorIcons"
          :style="{ color: activeBackgroundColor }"
          v-tooltip="{ content: $t('elementsTextTranslation.background'), theme: 'info-tooltip' }"
        ></i>
      </div>
      <div v-if="showBackgroundColors" class="modal">
        <span
          v-for="color in colors"
          :key="'background_' + color"
          role="button"
          class="colorSquare"
          :style="{ 'background-color': color }"
          @click="pickBackgroundColor(color)"
        ></span>
      </div>
      <!-- Blockquote -->
      <i
        :class="{ isActive: isActive(BLOCKQUOTE) }"
        class="fas fa-quote-right toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.blockquote'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().toggleBlockquote().run()"
      ></i>
      <!-- Hyperlinks -->
      <i
        :class="{ isActive: isActive(HYPERLINK) }"
        class="fas fa-link toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.link'), theme: 'info-tooltip' }"
        @click.prevent.stop="setLink()"
      ></i>
      <!-- Image -->
      <i
        :class="{ isActive: isActive(IMAGE) }"
        class="fas fa-image toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.image'), theme: 'info-tooltip' }"
        @click="showMediaModal"
      ></i>
      <!-- Unstyle -->
      <i
        class="fas fa-remove-format toolbarIcon"
        v-tooltip="{ content: $t('elementsTextTranslation.clean'), theme: 'info-tooltip' }"
        @click="editor.chain().focus().clearNodes().unsetAllMarks().run()"
      ></i>
      <!-- Reference -->
      <span
        v-tooltip="{ content: $t('elementsTextTranslation.reference'), theme: 'info-tooltip' }"
        class="toolbarReference"
        @click="showReferenceModal()"
      >
        {{}}
      </span>
    </div>

    <div class="textEditor backgroundColor" @click="editorFocus()">
      <EditorContent :editor="editor"></EditorContent>
    </div>

    <StudyMedia
      v-if="studyMediaVisible"
      :id="'mediaSelectionTextEditor' + propsObj.id"
      :allFiles="false"
      :isSelection="true"
      :studyId="studyId"
      :multiple="true"
      @close-modal="closeMediaModal(null)"
      @item-selected="closeMediaModal"
    ></StudyMedia>

    <BaseModal
      v-if="insertReferenceVisible"
      id="insertReferenceModal"
      :headerText="$t('referenceTranslation.insertReference')"
      :leftButtonText="$t('referenceTranslation.insert')"
      :disableLeftButton="insertLabel == '' && insertLabel !== 0"
      @close-modal="closeReferenceModal"
    >
      <template v-slot:body>
        <template v-if="questionLabelList?.length">
          {{ $t("referenceTranslation.referenceFor") }}<br>
          <select class="selectReference" v-model="insertLabel" :id="'inputReference' + propsObj.id">
            <option value="" disabled :id="'inputReference' + propsObj.id + 'None'">
              {{ $t("elementsBlockTranslation.selectLabel") }}
            </option>
            <option
              v-for="(option, index) in questionLabelList"
              :value="index"
              :id="'inputReference' + propsObj.id + '-' + index"
              :key="index"
            >
              {{ option.label }}&nbsp;
            </option>
          </select>
        </template>
        <p v-if="errorModal !== null" class="errorMessage" id="errorTextReference">
          {{ errorModal }}
        </p>
        <p
          v-else-if="!questionLabelList?.length"
          id="errorTextNoReference"
          class="errorMessage"
          style="margin-top: 10px;"
        >
          {{ $t("elementsBlockTranslation.noQuestions") }}
        </p>
      </template>
    </BaseModal>

    <BubbleMenu
      v-if="editor"
      :editor="editor"
      :tippy-options="{
        duration: 0,
        placement: 'bottom-start',
        appendTo: 'parent',
        onHide: handleTippyHidet,
        onShown: handleTippyShown,
      }"
      :should-show="shouldShowBubbleMenu"
      :class="{
        'link-bubble-menu': true,
        'is-active': editor && (editor.isActive('link') || isEditingLinkInline),
      }"
      :id="'linkBubbleMenu' + propsObj.id"
      @click.native.stop
    >
      <div v-if="editor.isActive('link') && !isEditingLinkInline" class="bubble-mode-view">
        <div class="bubble-view-row">
          <span class="bubble-label">{{ $t('elementsTextTranslation.visitUrl') || 'Visit URL:' }}</span>
          <a :href="linkUrl" target="_blank" class="bubble-url">{{ linkUrl }}</a>
        </div>
        <div class="bubble-view-row actions">
          <button type="button" class="bubble-action-btn" @click="editLinkInline">
            {{ $t('elementsTextTranslation.edit') || 'Edit' }}
          </button>
          <span class="bubble-separator">|</span>
          <button type="button" class="bubble-action-btn" @click="removeLinkInline">
            {{ $t('elementsTextTranslation.remove') || 'Remove' }}
          </button>
        </div>
      </div>
      <div v-else-if="isEditingLinkInline" class="bubble-mode-edit">
        <span class="bubble-label">{{ $t('elementsTextTranslation.enterLink') || 'Enter link:' }}</span>
        <input
          :id="'linkInputUrl' + propsObj.id"
          ref="linkInputUrlRef"
          type="text"
          v-model="linkInputUrl"
          class="bubble-input"
          @pointerdown="handleInputPointerDown"
          @keyup.enter="saveLinkInline"
          @keyup.esc="cancelLinkInline"
        />
        <button type="button" class="bubble-save-btn" @click="saveLinkInline">
          {{ $t('generalTranslation.save') }}
        </button>

        <div v-if="linkModalError" class="bubble-error" :id="'errorTextLink' + propsObj.id">
          {{ linkModalError }}
        </div>
        <div v-if="linkModalWarning" class="bubble-warning" :id="'warningTextLink' + propsObj.id">
          {{ linkModalWarning }}
        </div>
      </div>
    </BubbleMenu>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import BaseModal from "./BaseModal.vue";
import StudyMedia from "./StudyMedia.vue";
import { Editor, EditorContent, BubbleMenu } from "@tiptap/vue-2";
import { Color } from "@tiptap/extension-color";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Underline from "@tiptap/extension-underline";
import OfficePaste from "@intevation/tiptap-extension-office-paste";
import { TextAlign } from "../text_editor/Alignment";
import { Indentation } from "../text_editor/Indentation";
import { BackgroundColor } from "../text_editor/BackgroundColor";
import { Fonts } from "../text_editor/Fonts";
import { ResizableImage } from "../text_editor/ResizableImage";
import { TextStyle } from "../text_editor/TextStyle";
import { debounce } from "../utils/helper";
import utils from "../mixins/utils";

const CustomLink = Link.extend({
  inclusive: false,
});

export default {
  name: "TextEditor",
  mixins: [utils],

  components: {
    BaseModal,
    EditorContent,
    BubbleMenu,
    StudyMedia,
  },

  props: {
    value: {
      type: String,
      default: "",
    },

    propsObj: {
      required: true,
      type: Object,
    },

    studyId: {
      type: Number,
      default: null,
    },

    isTranslation: {
      type: Boolean,
      default: false,
    },
  },

  data: function() {
    return {
      editor: null,
      insertReferenceVisible: false,
      insertLabel: "",
      errorModal: null,
      activeIcons: [],
      timeoutId: null,
      showColors: false,
      showBackgroundColors: false,
      activeColor: "",
      activeBackgroundColor: "",
      studyMediaVisible: false,
      linkUrl: "",
      linkInputUrl: "",
      isEditingLinkInline: false,
      linkModalError: "",
      linkModalWarning: "",
      linkEditStartFrom: null,
      linkEditStartTo: null,
    };
  },

  created() {
    this.FONTS_ROBOTO = 100;
    this.FONTS_SERIF = 101;
    this.FONTS_SANS_SERIF = 102;
    this.FONTS_MONOSPACE = 103;
    this.FONTS_MANROPE = 104;
    this.FONTS_ABRIL_FATFACE = 105;

    this.HEADING_0 = 200;
    this.HEADING_1 = 201;
    this.HEADING_2 = 202;
    this.HEADING_3 = 203;
    this.HEADING_4 = 204;
    this.HEADING_5 = 205;
    this.HEADING_6 = 206;

    this.BOLD = 300;
    this.ITALIC = 301;
    this.UNDERLINE = 302;
    this.STRIKETHROUGH = 303;

    this.ALIGN_LEFT = 400;
    this.ALIGN_CENTER = 401;
    this.ALIGN_RIGHT = 402;
    this.ALIGN_JUSTIFY = 403;

    this.ORDERED_LIST = 500;
    this.UNORDERED_LIST = 501;
    this.SUBSCRIPT = 502;
    this.SUPERSCRIPT = 503;
    this.BLOCKQUOTE = 504;
    this.HYPERLINK = 505;
    this.IMAGE = 506;
  },

  mounted() {
    const starters = StarterKit.configure({
      hardBreak: false,
    });
    const image = ResizableImage.configure({
      inline: true,
    });
    const link = CustomLink.configure({
      openOnClick: false,
      defaultProtocol: "https",
      HTMLAttributes: {
        class: "linkColor",
      },
    });
    this.editor = new Editor({
      content: this.value,
      editorProps: {
        attributes: {
          class: "editorSize",
        },
      },
      parseOptions: {
        preserveWhitespace: false,
      },
      onUpdate: () => {
        this.$emit("input", this.editor.getHTML());
      },
      onSelectionUpdate: () => {
        const updateIcons = debounce(() => this.setActiveIcons(), this.timeoutId, 75);
        this.timeoutId = updateIcons();
      },
      onFocus: () => {
        // Close color popups when clicking inside the editor
        this.showColors = false;
        this.showBackgroundColors = false;
        // Re-run setActiveIcons after applying styles to a selection
        // If left out, necessary changes in the toolbar only show when re-selecting text
        const updateIcons = debounce(() => this.setActiveIcons(), this.timeoutId, 75);
        this.timeoutId = updateIcons();
      },
      extensions: [
        starters,
        OfficePaste,
        BackgroundColor,
        Indentation,
        Color,
        image,
        Fonts,
        link,
        Underline,
        Subscript,
        Superscript,
        TextAlign,
        TextStyle,
      ],
    });
  },

  computed: {
    ...mapGetters([
      "getIsEditable",
      "getQuestionLabelList",
    ]),

    questionLabelList: function() {
      return this.getQuestionLabelList.filter((option) => option && option !== "0");
    },

    fonts: function() {
      return [
        { active: this.FONTS_ROBOTO, name: "Roboto", class: "ql-font-roboto" },
        { active: this.FONTS_SERIF, name: "Serif", class: "ql-font-serif" },
        { active: this.FONTS_SANS_SERIF, name: "Sans-Serif", class: "ql-font-sansserif" },
        { active: this.FONTS_MONOSPACE, name: "Monospace", class: "ql-font-monospace" },
        { active: this.FONTS_MANROPE, name: "Manrope", class: "ql-font-manrope" },
        { active: this.FONTS_ABRIL_FATFACE, name: "Abril Fatface", class: "ql-font-abrilfatface" },
      ];
    },

    headings: function() {
      return [
        { active: this.HEADING_0, name: Vue.i18n.translate("elementsHeadlineTranslation.headlineType0"), level: 0, fontSize: "1em" },
        { active: this.HEADING_1, name: Vue.i18n.translate("elementsHeadlineTranslation.headlineType1"), level: 1, fontSize: "2em" },
        { active: this.HEADING_2, name: Vue.i18n.translate("elementsHeadlineTranslation.headlineType2"), level: 2, fontSize: "1.5em" },
        { active: this.HEADING_3, name: Vue.i18n.translate("elementsHeadlineTranslation.headlineType3"), level: 3, fontSize: "1.17em" },
        { active: this.HEADING_4, name: Vue.i18n.translate("elementsHeadlineTranslation.headlineType4"), level: 4, fontSize: "1em" },
        { active: this.HEADING_5, name: Vue.i18n.translate("elementsHeadlineTranslation.headlineType5"), level: 5, fontSize: "0.83em" },
        { active: this.HEADING_6, name: Vue.i18n.translate("elementsHeadlineTranslation.headlineType6"), level: 6, fontSize: "0.67em" },
      ];
    },

    allowedProtocols: function() {
      return ["http://", "https://", "mailto:"];
    },

    colors: function() {
      return [
        "#000000", "#E60000", "#FF9900", "#FFFF00", "#008A00", "#0066CC", "#9933FF",
        "#FFFFFF", "#FACCCC", "#FFEBCC", "#FFFFCC", "#CCE8CC", "#CCE0F5", "#EBD6FF",
        "#BBBBBB", "#F06666", "#FFC266", "#FFFF66", "#66B966", "#66A3E0", "#C285FF",
        "#888888", "#A10000", "#B26B00", "#B2B200", "#006100", "#0047B2", "#6B24B2",
        "#444444", "#5C0000", "#663D00", "#666600", "#003700", "#002966", "#3D1466",
      ];
    },
  },

  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },

  watch: {
    value(value) {
      if (this.editor.getHTML() !== value) {
        this.editor.commands.setContent(value, false);
      }
    },
  },

  methods: {
    toggleColorsModal() {
      this.showBackgroundColors = false;
      this.showColors = !this.showColors;
    },

    pickColor(color) {
      if (color === "#000000") {
        this.editor.chain().focus().unsetColor().run();
      } else {
        this.editor.chain().focus().setColor(color).run();
      }
      this.showColors = false;
    },

    toggleBackgroundColorsModal() {
      this.showColors = false;
      this.showBackgroundColors = !this.showBackgroundColors;
    },

    pickBackgroundColor(color) {
      if (color === "#FFFFFF") {
        this.editor.chain().focus().unsetBackgroundColor().run();
      } else {
        this.editor.chain().focus().setBackgroundColor(color).run();
      }
      this.showBackgroundColors = false;
    },

    changeFont(font) {
      this.editor.chain().focus().setFont(font).run();
    },

    changeHeading(heading) {
      const level = parseInt(heading, 10);
      if (level === 0) {
        // The current heading needs to be removed, if no heading was selected
        const headingLevel = this.editor.getAttributes("heading")?.level;
        if (headingLevel) {
          this.editor.chain().focus().toggleHeading({ level: headingLevel }).run();
        }
      } else {
        this.editor.chain().focus().setHeading({ level }).run();
      }
    },

    changeTextAlign(alignment) {
      if (alignment === "left") {
        this.editor.chain().focus().unsetTextAlign().run();
      } else {
        const cssClass = `ql-align-${alignment}`;
        if (this.editor.isActive({ textAlign: cssClass })) {
          this.editor.chain().focus().unsetTextAlign().run();
        } else {
          this.editor.chain().focus().setTextAlign(cssClass).run();
        }
      }
    },

    changeSubscript() {
      if (this.editor.isActive("superscript")) {
        this.editor.chain().focus().unsetSuperscript().run();
      }
      this.editor.chain().focus().toggleSubscript().run();
    },

    changeSuperscript() {
      if (this.editor.isActive("subscript")) {
        this.editor.chain().focus().unsetSubscript().run();
      }
      this.editor.chain().focus().toggleSuperscript().run();
    },

    escapeHtml(string) {
      return String(string)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    clearLinkModalMessages() {
      this.linkModalError = "";
      this.linkModalWarning = "";
    },

    shouldShowBubbleMenu({ editor }) {
      return editor.isActive("link") || this.isEditingLinkInline;
    },

    setLink() {
      this.clearLinkModalMessages();
      
      const { state } = this.editor;
      const { from, empty } = state.selection;

      if (this.editor.isActive("link")) {
        this.linkUrl = this.editor.getAttributes("link").href || "";
        this.linkInputUrl = this.linkUrl;
      } else {
        // if selection is empty, try to select the word under the cursor
        if (empty) {
          const $from = state.doc.resolve(from);
          const text = $from.parent.textContent;
          const offset = $from.parentOffset;
          const wordCharRegex = /[a-zA-Z0-9_\u00C0-\u00FF\u0100-\u017F]/;

          // find start of word
          let start = offset;
          while (start > 0 && wordCharRegex.test(text[start - 1])) {
            start--;
          }
          // find end of word
          let end = offset;
          while (end < text.length && wordCharRegex.test(text[end])) {
            end++;
          }

          if (start < end) {
            const wordStart = from - offset + start;
            const wordEnd = from - offset + end;
            this.editor.commands.setTextSelection({ from: wordStart, to: wordEnd });
          }
        }
        
        this.linkUrl = "";
        this.linkInputUrl = "https://";
      }
      this.isEditingLinkInline = true;

      const currentSelection = this.editor.state.selection;
      this.linkEditStartFrom = currentSelection.from;
      this.linkEditStartTo = currentSelection.to;

      this.editor.commands.focus();
    },

    editLinkInline() {
      this.clearLinkModalMessages();
      this.linkInputUrl = this.linkUrl;
      this.isEditingLinkInline = true;

      const currentSelection = this.editor.state.selection;
      this.linkEditStartFrom = currentSelection.from;
      this.linkEditStartTo = currentSelection.to;
      this.handleTippyShown();
    },

    cancelLinkInline() {
      this.isEditingLinkInline = false;
      this.clearLinkModalMessages();
      this.editor.commands.focus();
    },

    handleTippyHide() {
      this.isEditingLinkInline = false;
      this.clearLinkModalMessages();
    },

    handleTippyShown() {
      this.$nextTick(() => {
        if (this.isEditingLinkInline && this.$refs.linkInputUrlRef) {
          this.$refs.linkInputUrlRef.focus();
          this.$refs.linkInputUrlRef.select();
        }
      });
    },

    handleInputPointerDown(event) {
      event.target.setPointerCapture(event.pointerId);
    },

    removeLinkInline() {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      this.isEditingLinkInline = false;
      this.clearLinkModalMessages();
    },

    saveLinkInline() {
      let href = this.linkInputUrl.trim();

      if (!href) {
        this.linkModalError = this.$t("elementsTextTranslation.urlRequired") || "URL is required";
        return;
      }

      // check if protocol is supported (if protocol exists)
      const splitUrl = href.split(":");
      if (splitUrl.length >= 2) {
        if (this.allowedProtocols.every((p) => !href.startsWith(p))) {
          this.linkModalError = Vue.i18n.translate(
            "elementsTextTranslation.unsupportedProtocolAlert",
            { protocols: this.allowedProtocols.join(", ") }
          );
          this.linkModalWarning = "";
          return;
        }
      }

      // validate structure based on protocol
      if (href.startsWith("mailto:")) {
        const emailAddress = href.substring(7);
        if (this.isInvalidEmail(emailAddress)) {
          this.linkModalError = this.$t("elementsTextTranslation.invalidEmail") || "Invalid email format.";
          this.linkModalWarning = "";
          return;
        }
      } else {
        let hasPrefixAdded = false;
        if (splitUrl.length < 2) { // no protocol
          href = "https://" + href;
          hasPrefixAdded = true;
        }

        // Validate web URL regex
        const webUrlRegex = /^https?:\/\/([a-zA-Z0-9][-a-zA-Z0-9]*\.)+[a-zA-Z]{2,}(:\d+)?([/?#].*)?$/i;
        if (!webUrlRegex.test(href)) {
          this.linkModalError = this.$t("elementsTextTranslation.invalidUrl") || "Invalid URL format.";
          this.linkModalWarning = "";
          return;
        }

        // show protocol warning if it was missing initially
        if (hasPrefixAdded) {
          const a = document.createElement("a");
          a.href = href;

          let warningMsg = "";
          if (a.host === false || (a.host === window.location.host && !href.match(a.host))) {
            warningMsg = Vue.i18n.translate("elementsTextTranslation.linkAlert", { href: a.href });
          } else {
            warningMsg = Vue.i18n.translate("elementsTextTranslation.missingProtocolAlert", { href: a.href });
          }

          // show warning. if warning is already displayed and the URL matches the warning destination, we allow save.
          if (this.linkModalWarning !== warningMsg) {
            this.linkInputUrl = href; // Update the URL input
            this.linkModalWarning = warningMsg;
            this.linkModalError = "";
            return;
          }
        }
      }

      // apply the link
      if (this.editor.isActive("link")) {
        this.editor.chain().focus().extendMarkRange("link").run();
        const { to } = this.editor.state.selection;
        this.editor.chain().focus()
          .setLink({ href })
          .setTextSelection({ from: to, to: to })
          .run();
      } else if (this.editor.state.selection.empty) {
        this.editor.chain().focus()
          .insertContent(`<a href="${this.escapeHtml(href)}">${this.escapeHtml(href)}</a>`)
          .run();
      } else {
        const { to } = this.editor.state.selection;
        this.editor.chain().focus()
          .setLink({ href })
          .setTextSelection({ from: to, to: to })
          .run();
      }
      this.isEditingLinkInline = false;
      this.clearLinkModalMessages();
    },

    showMediaModal() {
      this.studyMediaVisible = true;
      this.$emit("showMediaReferenceModal", true);
    },

    closeMediaModal(media) {
      try {
        for (const file in media) {
          const src = media[file].link;
          this.editor.chain().focus().setImage({ src }).run();
        }
      } catch (error) {
        console.error(error);
        alert(Vue.i18n.translate("elementsTextTranslation.mediaError"));
      } finally {
        this.studyMediaVisible = false;
        this.$emit("showMediaReferenceModal", false);
      }
    },

    showReferenceModal() {
      this.insertReferenceVisible = true;
      this.$emit("showMediaReferenceModal", true);
    },

    closeReferenceModal(done) {
      if (done) {
        if (this.insertLabel != "" || this.insertLabel === 0) {
          this.errorModal = null;
          const position = this.editor.state.selection.$anchor.pos;
          const questionLable = this.questionLabelList[this.insertLabel];
          if (questionLable != 0) {
            const lableToInsert = "{{" + questionLable.label + "}}";
            this.editor.commands.insertContentAt(position, lableToInsert);
          }
        } else {
          this.errorModal = Vue.i18n.translate("elementsBlockTranslation.errorLabel");
        }
      }
      this.insertReferenceVisible = false;
      this.$emit("showMediaReferenceModal", false);
    },

    editorFocus() {
      // Only set focus, if editor isn't focused already
      if (document.activeElement !== this.editor.view.dom) {
        this.editor.commands.focus("end");
      }
    },

    setActiveIcons() {
      const active = [];
      const textStyles = this.editor.getAttributes("textStyle");
      const activeFont = textStyles.font;
      if (activeFont) {
        const font = this.fonts.find((f) => f.class === activeFont);
        if (font) {
          active.push(font.active);
        }
      }
      const headingLevel = this.editor.getAttributes("heading")?.level;
      if (headingLevel) {
        const heading = this.headings.find((h) => h.level === headingLevel);
        if (heading) {
          active.push(heading.active);
        }
      }
      if (this.editor.isActive("bold")) {
        active.push(this.BOLD);
      }
      if (this.editor.isActive("italic")) {
        active.push(this.ITALIC);
      }
      if (this.editor.isActive("underline")) {
        active.push(this.UNDERLINE);
      }
      if (this.editor.isActive("strike")) {
        active.push(this.STRIKETHROUGH);
      }
      if (this.editor.isActive({ textAlign: "ql-align-center" })) {
        active.push(this.ALIGN_CENTER);
      } else if (this.editor.isActive({ textAlign: "ql-align-right" })) {
        active.push(this.ALIGN_RIGHT);
      } else if (this.editor.isActive({ textAlign: "ql-align-justify" })) {
        active.push(this.ALIGN_JUSTIFY);
      } else {
        active.push(this.ALIGN_LEFT);
      }
      if (this.editor.isActive("orderedList")) {
        active.push(this.ORDERED_LIST);
      }
      if (this.editor.isActive("bulletList")) {
        active.push(this.UNORDERED_LIST);
      }
      if (this.editor.isActive("subscript")) {
        active.push(this.SUBSCRIPT);
      }
      if (this.editor.isActive("superscript")) {
        active.push(this.SUPERSCRIPT);
      }
      if (this.editor.isActive("blockquote")) {
        active.push(this.BLOCKQUOTE);
      }
      if (this.editor.isActive("link")) {
        active.push(this.HYPERLINK);
        this.linkUrl = this.editor.getAttributes("link").href || "";
      } else {
        if (!this.isEditingLinkInline) {
          this.linkUrl = "";
        }
      }
      if (this.editor.isActive("ResizableImage")) {
        active.push(this.IMAGE);
      }
      this.activeIcons = active;
      this.activeColor = textStyles.color;
      this.activeBackgroundColor = textStyles.backgroundColor;

      if (this.isEditingLinkInline) {
        const { from, to } = this.editor.state.selection;
        if (from !== this.linkEditStartFrom || to !== this.linkEditStartTo) {
          if (document.activeElement !== this.$refs.linkInputUrlRef) {
            this.isEditingLinkInline = false;
          }
        }
      }
    },

    isActive(icon) {
      return this.activeIcons.includes(icon);
    },
  },
};
</script>

<style scoped>
/* Remove focus outline */
:deep(.ProseMirror:focus) {
  outline: 0;
}

.isActive {
  color: var(--colorActive) !important;
}
.toolbarIcon {
  line-height: 1.8em;
  cursor: pointer;
  margin-left: 6px;
  margin-right: 6px;
  color: var(--colorDark);
}
.toolbar {
  width: var(--text-element-width);
  min-width: 410px;
  max-width: 1200px;
  border: 1px solid var(--colorInputBorder);
  padding-top: 3px;
  padding-bottom: 3px;
}
.backgroundColor {
  background-color: white;
}
.textEditor {
  width: var(--text-element-width);
  min-width: 410px;
  max-width: 1200px;
  min-height: 200px;
  font-size: 16px;
  tab-size: 4;
  border: 1px solid var(--colorInputBorder);
  height: 350px;
  overflow-y: auto;
  cursor: text;
}
.toolbarSelect {
  margin-left: 6px;
  margin-right: 6px;
  margin-top: 5px;
  margin-bottom: 0px;
  padding: 3px;
  padding-right: 20px;
}
.toolbarReference {
  margin-bottom: 0px;
  background: 0 0;
  color: var(--colorDark);
  cursor: pointer;
}
.selectReference {
  max-width: 99%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}
.modal {
  position: absolute;
  margin-top: 30px;
  margin-left: -15px !important;
  z-index: 1;
  width: 152px;
  display: inline-block;
  align-items: center;
  justify-content: center;
  background-color: white;
  color: #444444;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid transparent;
  box-shadow: rgba(0,0,0,.2) 0 2px 8px;
  padding: 3px 5px;
  box-sizing: border-box;
  white-space: no-wrap;
  border-color: var(--colorInputBorder);
}
.colorSquare {
  border: 1px solid transparent;
  float: left;
  height: 16px;
  width: 16px;
  margin: 2px;
  padding: 0;
  cursor: pointer;
  border-color: var(--colorInputBorder);
}
.colorIcons {
  text-shadow: 0px 0px 2px var(--colorInputBorder);
}
.colorIconsActive {
  border: 1px solid transparent;
  border-color: var(--colorInputBorder);
}
.colorIconsInactive {
  border: 1px solid transparent;
  border-color: none;
}
.iconBox {
  display: inline-block;
  cursor: pointer;
}
.imageButtons {
  border: 2px solid transparent;
  border-color: var(--colorActive);
}
</style>
